import { useState } from "react";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Send, Mail, MapPin, Phone } from "lucide-react";

const SERVICES = [
  { value: "Application Development", label: "Application Development" },
  { value: "Digital Business Transformation", label: "Business Digital Transformation" },
  { value: "Agentic AI", label: "Agentic AI" },
  { value: "IOT / Robotics", label: "Internet of Things / Robotics" },
  { value: "General / Others", label: "General / Other" },
];

const EMPTY = { name: "", email: "", company: "", service: "General / Others", message: "" };

export default function Contact() {
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Complete required fields to transmit.");
      return;
    }
    setLoading(true);
    try {
      const formData = new URLSearchParams();
      formData.append("entry.94397111", form.name);
      formData.append("entry.2059747203", form.email);
      formData.append("entry.687378288", form.service);
      
      let finalMessage = form.message;
      if (form.company) {
        finalMessage = `Organization: ${form.company}\n\nMessage: ${form.message}`;
      }
      formData.append("entry.1471834227", finalMessage);

      await fetch("https://docs.google.com/forms/d/e/1FAIpQLSePg_FWx5U2Yq5_iyfx6Oin4GYZVUJhPn5n6ajA-6FFy1voUA/formResponse", {
        method: "POST",
        mode: "no-cors",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
        },
        body: formData.toString(),
      });

      toast.success("TRANSMISSION RECEIVED // LAB WILL RESPOND WITHIN 48H");
      setForm(EMPTY);
    } catch (err) {
      toast.error("Transmission failed. Retry.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fade-up" data-testid="page-contact">
      <section className="relative pt-16 pb-16 border-b border-[#1E2BFF]/20">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="overline mb-5">// ENGAGE.PROTOCOL</div>
          <h1 className="font-hero text-5xl lg:text-8xl font-black uppercase tracking-tighter text-white leading-[0.95]">
            Open a <br />
            <span className="text-[#00F0FF] glow-cyan">channel.</span>
          </h1>
          <p className="mt-8 max-w-2xl text-[#9BA1D6] text-lg leading-relaxed">
            Brief us on the problem. We respond within 48 hours with a pointed
            next step — not a sales deck.
          </p>
        </div>
      </section>

      <section className="relative py-20">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-10">
          {/* Form */}
          <motion.form
            onSubmit={submit}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="lg:col-span-7 hud-card corner-brackets p-8 lg:p-12"
            data-testid="contact-form"
          >
            <div className="flex items-center justify-between mb-8">
              <div className="overline">// FORM.TRANSMIT</div>
              <span className="pulse-dot" />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <Field label="Operator Name *">
                <input
                  name="name"
                  required
                  value={form.name}
                  onChange={onChange}
                  placeholder="JANE DOE"
                  className="hud-input"
                  data-testid="contact-input-name"
                />
              </Field>
              <Field label="Email *">
                <input
                  name="email"
                  type="email"
                  required
                  value={form.email}
                  onChange={onChange}
                  placeholder="OPERATOR@DOMAIN.IO"
                  className="hud-input"
                  data-testid="contact-input-email"
                />
              </Field>
              <Field label="Organization">
                <input
                  name="company"
                  value={form.company}
                  onChange={onChange}
                  placeholder="ACME CORP"
                  className="hud-input"
                  data-testid="contact-input-company"
                />
              </Field>
              <Field label="Service Interest">
                <select
                  name="service"
                  value={form.service}
                  onChange={onChange}
                  className="hud-input font-mono tracking-[0.15em] text-[12px]"
                  data-testid="contact-input-service"
                >
                  {SERVICES.map((s) => (
                    <option key={s.value} value={s.value} className="bg-[#05060F] text-white">
                      {s.label}
                    </option>
                  ))}
                </select>
              </Field>
            </div>

            <div className="mt-5">
              <Field label="Mission Brief *">
                <textarea
                  name="message"
                  required
                  rows={6}
                  value={form.message}
                  onChange={onChange}
                  placeholder="DESCRIBE THE SYSTEM TO BUILD..."
                  className="hud-input resize-y min-h-[140px]"
                  data-testid="contact-input-message"
                />
              </Field>
            </div>

            <div className="mt-8 flex items-center justify-between gap-4">
              <div className="font-mono text-[10px] tracking-[0.25em] uppercase text-[#9BA1D6]">
                {loading ? "// encrypting..." : "// encrypted channel / avinya.io"}
              </div>
              <button
                type="submit"
                disabled={loading}
                className="btn-primary disabled:opacity-60"
                data-testid="contact-submit-btn"
              >
                {loading ? "Transmitting" : "Transmit"} <Send size={16} />
              </button>
            </div>
          </motion.form>

          {/* Coordinates panel */}
          <motion.aside
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="lg:col-span-5 space-y-5"
          >
            <div className="hud-card corner-brackets p-8">
              <div className="overline mb-6">// COORDINATES.ROOT</div>
              <ul className="space-y-6">
                <Contact3
                  icon={Mail}
                  label="Email"
                  value="contact@avinyaresearch.in"
                />
                <Contact3
                  icon={Phone}
                  label="Hotline"
                  value="+91 7019199645"
                />
                <Contact3
                  icon={MapPin}
                  label="HQ"
                  value={`Remote First - Pan India`}
                />
              </ul>
              <div className="mt-8 pt-6 border-t border-dashed border-[#1E2BFF]/30 font-mono text-[10px] tracking-[0.3em] uppercase text-[#9BA1D6]">
                RESPONSE SLA // 48 HRS
              </div>
            </div>

            <div className="hud-card p-6">
              <div className="overline mb-3">// LAB.STATUS</div>
              <div className="flex items-center gap-3 mb-3">
                <span className="pulse-dot" />
                <span className="font-mono text-[11px] tracking-[0.25em] uppercase text-white">
                  All systems online
                </span>
              </div>
              <div className="ticks h-16 mt-4" />
              <div className="mt-4 font-mono text-[10px] tracking-[0.3em] uppercase text-[#00F0FF]">
                ACCEPTING NEW ENGAGEMENTS // Q1 2026
              </div>
            </div>
          </motion.aside>
        </div>
      </section>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="font-mono text-[10px] tracking-[0.28em] uppercase text-[#9BA1D6] mb-2 block">
        {label}
      </span>
      {children}
    </label>
  );
}

function Contact3({ icon: Icon, label, value }) {
  return (
    <li className="flex items-start gap-4">
      <div className="w-10 h-10 border border-[#1E2BFF]/40 flex items-center justify-center flex-shrink-0 mt-1">
        <Icon size={16} className="text-[#00F0FF]" />
      </div>
      <div>
        <div className="font-mono text-[10px] tracking-[0.28em] uppercase text-[#9BA1D6]">
          {label}
        </div>
        <div className="text-white font-medium mt-1 whitespace-pre-line">{value}</div>
      </div>
    </li>
  );
}
